import styled, { css } from 'styled-components'
import { CarouselProvider, Slider, Slide } from 'pure-react-carousel'
import { desktopBreakpoint, tabletBreakpoint } from 'react-components'

export const Container = styled.div(
    ({ theme }) => css`
        background: #eff7ff;
        width: 100%;
        margin-top: ${theme.spacing.medium};
        position: relative;
        overflow: hidden;

        &::before {
            content: '';
            position: absolute;
            top: -1rem;
            left: 0;
            width: 100%;
            height: 2rem;
            background: white;
        }

        @media only screen and (min-width: ${desktopBreakpoint.min}px) {
            width: 100vw;
            margin-left: 50%;
            transform: translateX(-50%);
            position: relative;

            &::before {
                width: 100vw;
                margin-left: -50vw;
                left: 50%;
            }
        }
    `,
)

export const InnerContainer = styled.div`
    @media only screen and (min-width: ${desktopBreakpoint.min}px) {
        max-width: 87.5rem;
        margin: 0 auto;
        position: relative;
    }
`

export const HeadingWrapper = styled.div(
    ({ theme }) => css`
        text-align: left;
        padding: ${theme.spacing.xxxLarge} ${theme.spacing.large} 1.25rem;
    `,
)

export const Heading = styled.h2(
    ({ theme }) => css`
        margin: 0 0 ${theme.spacing.xxSmall};
        font-family: ${theme.typography.font.family.latoVerdana};
        font-weight: 600;
        font-size: ${theme.typography.font.size.large};
        line-height: 1.75rem;
        color: ${theme.colors.tfwBlack99};

        @media only screen and (min-width: ${tabletBreakpoint.min}px) {
            font-size: 1.75rem;
            line-height: 2.625rem;
        }

        @media only screen and (min-width: ${desktopBreakpoint.min}px) {
            font-size: 1.5625rem;
            line-height: 1.875rem;
        }
    `,
)

export const Subheading = styled.p(
    ({ theme }) => css`
        margin: 0;
        font-family: ${theme.typography.font.family.latoVerdana};
        font-weight: ${theme.typography.font.weight.normal};
        font-size: ${theme.typography.font.size.medium};
        line-height: 1.5rem;
        color: #5f5f5f;
    `,
)

export const StyledCarouselProvider = styled(CarouselProvider)(
    () => css`
        width: 100%;
        li {
            list-style: none;
        }
    `,
)

export const StyledSlider = styled(Slider)`
    padding: 0;
    ul {
        transition: transform 0.5s;
        transition-timing-function: cubic-bezier(0.645, 0.045, 0.355, 1);
        will-change: transform;
    }

    & > .carousel__slider-tray-wrapper {
        overflow: hidden;
    }
`

export const StyledSlide = styled(Slide)(
    ({ theme }) => css`
        padding-right: ${theme.spacing.medium};

        > div:first-child {
            height: 100%;
        }
    `,
)

export const CardsContainer = styled.div(
    ({ theme }) => css`
        padding: 0.4rem ${theme.spacing.medium} ${theme.spacing.xxLarge};

        @media only screen and (min-width: ${tabletBreakpoint.min}px) {
            padding: 0 ${theme.spacing.medium} ${theme.spacing.xxLarge};
        }

        @media only screen and (min-width: ${desktopBreakpoint.min}px) {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: ${theme.spacing.xxLarge};
            padding: 0 2.75rem 4rem;
            grid-template-rows: repeat(auto-fit, minmax(43.75rem, auto));
        }
    `,
)

export const TipCard = styled.a(
    ({ theme }) => css`
        background: ${theme.colors.white};
        border-radius: 0.75rem;
        border: ${theme.typography.border.width.xxxThin} ${theme.typography.border.style.solid}
            ${theme.colors.tfwBlackOpacity10};
        overflow: hidden;
        display: flex;
        flex-direction: column;
        height: 100%;
        box-shadow: 0 0.125rem 0.5rem rgba(0, 0, 0, 0.08);
        transition: box-shadow 0.2s ease-in-out;
        text-decoration: none;
        color: inherit;
        cursor: pointer;

        &:hover {
            box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.12);
        }
    `,
)

export const TipCardImage = styled.div(
    () => css`
        width: 100%;
        height: 12.5rem;
        position: relative;
        overflow: hidden;

        @media only screen and (min-width: ${tabletBreakpoint.min}px) {
            height: 13.75rem;
        }

        @media only screen and (min-width: ${desktopBreakpoint.min}px) {
            height: 17.5rem;
        }
    `,
)

export const TipCardContent = styled.div(
    ({ theme }) => css`
        padding: ${theme.spacing.large};
        display: flex;
        flex-direction: column;
        gap: ${theme.spacing.medium};
        flex: 1;

        @media only screen and (min-width: ${desktopBreakpoint.min}px) {
            justify-content: space-between;
        }
    `,
)

export const TipCardTitle = styled.h3(
    ({ theme }) => css`
        margin: 0;
        font-family: ${theme.typography.font.family.latoVerdana};
        font-weight: ${theme.typography.font.yummo.weight.bold};
        font-size: ${theme.typography.font.size.medium};
        line-height: 1.5rem;
        color: ${theme.colors.tfwBlack87};

        @media only screen and (min-width: ${tabletBreakpoint.min}px) {
            font-size: ${theme.typography.font.size.large};
            line-height: 1.75rem;
        }
    `,
)

export const TipCardDescription = styled.div(
    ({ theme }) => css`
        font-family: ${theme.typography.font.family.lato};
        font-weight: ${theme.typography.font.weight.normal};
        font-size: ${theme.typography.font.size.small};
        line-height: 1.6;
        color: ${theme.colors.tfwBlack87};
        flex: 1;

        p {
            margin: 0 0 ${theme.spacing.small};

            &:last-child {
                margin-bottom: 0;
            }
        }

        strong {
            font-weight: ${theme.typography.font.weight.bold};
        }

        a {
            color: #016bd4;
            text-decoration: underline;
            transition: color 0.2s ease-in-out;

            &:hover {
                color: #0159b8;
                text-decoration: underline;
            }

            &:visited {
                color: #016bd4;
            }

            &:active {
                color: #0159b8;
            }

            &:focus {
                outline: 0.125rem solid #016bd4;
                outline-offset: 0.125rem;
            }
        }

        @media only screen and (min-width: ${tabletBreakpoint.min}px) {
            font-size: ${theme.typography.font.size.medium};
        }

        @media only screen and (min-width: ${desktopBreakpoint.min}px) {
            overflow: auto;
        }
    `,
)

export const TipCardButton = styled.span(
    ({ theme }) => css`
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: ${theme.spacing.medium} 6rem;
        background-color: ${theme.colors.white};
        border: 0.0625rem solid ${theme.colors.tfwBlack87};
        border-radius: ${theme.typography.border.radius.medium};
        font-family: ${theme.typography.font.family.lato};
        font-weight: ${theme.typography.font.weight.normal};
        font-size: ${theme.typography.font.size.small};
        color: ${theme.colors.tfwBlack87};
        text-align: center;
        transition: all 0.3s ease-in-out;
        white-space: nowrap;
        width: 100%;

        &:active {
            background-color: ${theme.colors.white} !important;
            color: ${theme.colors.tfwBlack87};
            border-color: ${theme.colors.tfwBlack87};
        }

        &:focus {
            outline: none;
            background-color: ${theme.colors.white};
            color: ${theme.colors.tfwBlack87};
        }

        &:hover:not(:focus) {
            background-color: ${theme.colors.tfwBlack87};
            color: ${theme.colors.white};
            border-color: ${theme.colors.tfwBlack87};
        }

        @media (hover: none) and (pointer: coarse) {
            &:hover {
                background-color: ${theme.colors.white};
                color: ${theme.colors.tfwBlack87};
                border-color: ${theme.colors.tfwBlack87};
            }
        }

        @media only screen and (min-width: ${tabletBreakpoint.min}px) {
            font-size: ${theme.typography.font.size.medium};
        }
    `,
)
